import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { ChevronRightIcon, EllipsisVerticalIcon } from 'lucide-react'
import { NavStack, NavStackStage, NavStackScreen } from '@/components/custom/route-transition/stack/nav-stack'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { Button } from '@/components/ui/button'
import { SlideInPanel, ScrollableArea } from './-components/shared/layout'
import { burnCommitCost, ItemScreenTitle, useCommitCost } from './-components/dev/transition-playground'

// The HIERARCHICAL member of the transition playground (see dev-resources.transitions.tsx): a two-level
// nav stack exactly like a section's — level 0 an item list, level 1 the pushed item screen, which hosts
// the INNER lateral pager (dev-resources.transitions.one.item.tsx). One `levels` declaration is the whole
// hierarchy: the engine asks it of the rendered pathname AND the one being navigated to (so pushes are eager
// while pops keep their content), and of a tapped row's target (so the travel starts on the tap's own frame).
//
// LEVEL 1 IS BUILT LIKE A SECTION'S, NOT LIKE A DEMO'S, and that is the whole difference between a playground
// that reproduces the app's bugs and one that quietly cannot. The pop artefact reported from a device (the
// popped screen flashing over the list) could not appear here while this file rendered a static title and a
// bare `<Outlet/>`, because four things a real section has were missing:
//
//   1. A SCREEN THAT OUTLIVES ITS ROUTE. `<Outlet/>` alone empties itself with the navigation; a section's
//      level keeps a header (title + actions) and a pane wrapper that only empty when the ROUTE COMMIT lands —
//      which, since the pop's navigation is deferred a task, is 100ms+ after the stack has handed over.
//   2. A HEADER WITH SOMETHING IN IT. On Jobs the artefact WAS the level's action menu, drawn over the list's
//      own header. A screen whose header is a constant string has nothing to give away.
//   3. THE SECTION'S NESTING. A level sits inside the desktop grouping wrapper (`SlideInPanel`, made
//      `display: contents` on mobile by `ns-passthrough`), not directly in the stage.
//   4. A COMMIT THAT COSTS SOMETHING. Every route here resolves synchronously, so the deferred window is a
//      frame instead of the tenths of a second a report's subtree takes. `burnCommitCost` is that knob (see
//      the HUD's "commit cost" control), and it is what makes the window big enough to see and to measure.
export const Route = createFileRoute('/_app/dev-resources/transitions/one')({
  component: RouteComponent,
})

const ITEM_OPEN = /\/transitions\/one\/item(\/|$)/

/**
 * Is the item screen the RENDERED route? Read from the matched routes, which is the same timing a section gets
 * from `useParams({ strict: false })` — both come from `state.matches`, so both flip on the route COMMIT and
 * not a moment before. That timing is the thing being reproduced: it is what leaves a popped level full of
 * content while the stack has already moved on.
 */
function useItemOpen(): boolean {
  return useRouterState({ select: (state) => state.matches.some((match) => ITEM_OPEN.test(match.pathname)) })
}

function ItemList(props: { itemOpen: boolean }) {
  // The list is what a pop commits INTO, so the cost of that commit belongs here — the same place a section
  // pays it, re-rendering its rows because the selection changed. It takes `itemOpen` for exactly that reason:
  // a component that depends on nothing is memoized by React Compiler and skipped by the commit that matters.
  burnCommitCost(useCommitCost())
  return (
    <div className="flex flex-col gap-2 p-4">
      <p className="text-muted-foreground text-sm">
        Level 0 — the stack root{props.itemOpen ? ' (an item is open above it)' : ''}. A row pushes the item screen,
        whose tabs are the inner lateral pager.
      </p>
      {Array.from({ length: 3 }, (_, i) => (
        <Link
          key={i}
          to="/dev-resources/transitions/one/item"
          className="hover:bg-accent flex items-center justify-between rounded-md border p-3 text-sm"
        >
          Playground item {i + 1}
          <ChevronRightIcon className="text-muted-foreground size-4" />
        </Link>
      ))}
    </div>
  )
}

/** The level's trailing header controls — the shape of Jobs' `JobScreenActions`, which is what flashed. */
function ItemScreenActions() {
  return (
    <Button variant="ghost" size="icon" aria-label="Item actions" className="size-8">
      <EllipsisVerticalIcon className="size-4" />
    </Button>
  )
}

function RouteComponent() {
  const navigate = useNavigate()
  const itemOpen = useItemOpen()
  // Live previews route through here too, and which one decides what this renders: the INNER pager's preview is
  // of a surface BELOW us, so fall through to the item layout's own gate.
  //
  // AN `above` PREVIEW RENDERS THE WHOLE LEVEL, chrome included — the same thing every section does (jobs, teams,
  // offers, orgs gate `passthrough` and nothing else), and the reason is that a level's header is CONTENT, not
  // chrome-around-a-pane. This used to return the bare `<ItemList/>` ("no stack chrome, no observers"), and the
  // outer pager previews `one` into its incoming slot — so a swipe from Two showed the list with NO header for the
  // whole travel, 48px higher than it belongs, and the title appeared with the reveal while every row dropped a
  // header's height. Reported from the page as "the title shows up after the interaction", and it was: the preview
  // and the live level were two different pictures, which is the one thing a live preview exists not to be.
  const preview = usePreviewMode('/dev-resources/transitions/one')
  if (preview === 'passthrough') return <Outlet />
  return (
    // The playground carries the real thing, eager push included — it is where the stack is driven by hand.
    <NavStack levels={[true, ITEM_OPEN]} onPop={() => void navigate({ to: '/dev-resources/transitions/one' })}>
      {/* On a phone the stack's layers are `position: absolute; inset: 0` — the stage must own a real
          height (the section layouts get theirs from the app shell; this playground sets its own). */}
      <NavStackStage className="flex w-full overflow-hidden max-md:h-[65dvh]" style={{ containerType: 'inline-size' }}>
        <NavStackScreen index={0} title="Playground items">
          <ItemList itemOpen={itemOpen} />
        </NavStackScreen>
        {/* The section's own nesting, verbatim: the desktop grouping wrapper, which `ns-passthrough` turns into
            no box at all on mobile so the level inside positions against the stage. */}
        <SlideInPanel isOpen={itemOpen} innerClassName="flex flex-col" className="ns-passthrough">
          <NavStackScreen
            index={1}
            title={<ItemScreenTitle open={itemOpen} />}
            actions={itemOpen ? <ItemScreenActions /> : null}
          >
            {/* Gated on the RENDERED route, like every section: the pane empties on the route commit, which is
                a task (and on a phone a long one) after the pop handed the stack over. */}
            <ScrollableArea>{itemOpen && <Outlet />}</ScrollableArea>
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </NavStack>
  )
}
